<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { get } from 'svelte/store';

    import './AssignmentJobOne.css';
    import type { DisplayModeType, DisplayShapeType } from './types';
    import RoutePointMark from './RoutePointMark.svelte';
    import { Address } from './Address';
    import Opacity from '../../../shared/Opacity.svelte';
    import RoutePointEta from './RoutePointEta.svelte';
    import Duration from '../../../shared/Duration.svelte';
    import type { Assignment } from '../Assignment';
    import TimeWindowNotification from '../TimeWindowNotification.svelte';
    import { type RoutePointAlertType, type RoutePointAlertsType } from '../AssignmentApi';
    import { VehicleLimitItem } from '../VehicleLimit/VehicleLimitItem';
    import VehicleLimitExceededIcon from '../VehicleLimitExceededIcon.svelte';
    import VehicleLimitPie from '../VehicleLimitPie.svelte';
    import { rsa } from './RouteSummaryApp/rsa';
    import { leftPageWidthPx } from '../../../../stores/store';
    import { features } from '../../../shared/Features/features';
    import ButtonSmallBlueRounded from '../../../shared/ButtonSmallBlueRounded.svelte';
    import IconExpander from '../../../shared/Icons/IconExpander.svelte';
    import IconRemoveWarning from '../../../shared/Icons/IconRemoveWarning.svelte';

    export let assignment: Assignment | null = null;
    export let routePointIndex: number | null = null;
    export let simpleView: boolean = true;
    export let displayMode: DisplayModeType = 'routePoint';
    export let displayShape: DisplayShapeType = 'single-first';
    export let address: Address;
    export let eta_seconds: number;
    export let minimizeViewTimeWindow: boolean = false;
    export let removable: boolean = false;
    export let onRemove: (routePointIndex: number) => void = () => {};
    export let routeDirty: boolean = false;
    export let expandable: boolean = false;
    export let expanded: boolean = true;
    export let onToggle: (routePointIndex: number, expanded: boolean) => void = () => {};

    let alerts: RoutePointAlertsType = {};
    let vehicleLimits: any[] = [];

    let flexColumn: boolean = $leftPageWidthPx < 800 || $features.feature_configurable_job_description;

    $: {
        if (assignment && typeof routePointIndex === 'number') {
            let routeInfo = assignment.getRouteInfo();
            if (routeInfo) {
                let routeAlerts = routeInfo.alerts;
                if (
                    $rsa.selectedRouteType === 'current_in_realisation' &&
                    assignment.getRouteInRealisation()
                ) {
                    routeAlerts = assignment.getRouteInRealisation()?.alerts;
                } else if (routeInfo.reorder_stack?.length > 0) {
                    routeAlerts =
                        routeInfo.reorder_stack[routeInfo.reorder_stack.length - 1].alerts;
                }
                let limit_dict: any = { parcels: 'parcel_limit', total_weight: 'weight_limit' };
                vehicleLimits = [];
                if (routeAlerts) {
                    for (let type in limit_dict) {
                        let alertsOfType: RoutePointAlertType[] =
                            routeAlerts[type as keyof typeof routeAlerts];
                        if (
                            type != 'time_window' &&
                            alertsOfType &&
                            alertsOfType[routePointIndex]
                        ) {
                            let alert: RoutePointAlertType = alertsOfType[routePointIndex];
                            alerts[type as keyof typeof alerts] = alert;
                            if (!alert.fulfilled) {
                                let limitItem = VehicleLimitItem.getVehicleLimit(
                                    limit_dict[type],
                                    alert.value || 0,
                                    alert.limit
                                );
                                if (limitItem) {
                                    vehicleLimits.push(limitItem);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // convert eta_hour "23:33" to seconds
    // let eta_seconds = 0;
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="list-element delivery-list-element-checkbox list-element-data"
    class:minimizeViewTimeWindow
    class:delivery-list-element-routePoint={displayMode === 'routePoint'}
    class:simple-view={simpleView}
    style="position: static"
    on:click={evt => {}}
>
    <div class="checkbox" title={displayShape}>
        {#if displayMode === 'routePoint'}
            <RoutePointMark {displayShape} value={'M'} isWarehouse={true} />
        {/if}
    </div>

    <div class="color">
        <div style="display: flex; justify-content: flex-end; align-items: center;">
            <div style="width:50px;">
                {#if !routeDirty}
                    <Duration seconds={eta_seconds} displayFormat="time" />
                {/if}
            </div>
            <div style="width:21px;"></div>
        </div>
    </div>
    <div
        class="address-2 dont-break"
        class:row={simpleView}
        style="display: flex; {flexColumn ? 'flex-direction: column; align-items: start; justify-content: center;' : 'flex-direction: row; align-items: center; justify-content: flex-start;'};"
    >
        {#if simpleView}
            {#if address.city || address.street || address.name}
                {#if address.name}
                    <div class="city bold">
                        {address.name}
                    </div>
                {/if}
                {#if address.street || address.house}
                    <div
                        class="street light"
                        style="flex: 0 1 auto; text-overflow: ellipsis; white-space: nowrap; overflow: hidden;"
                    >
                        {#if address.city}
                            {address.city}{#if address.street},{/if}
                        {/if}
                        {address.street}
                        {address.house}
                    </div>
                {/if}
            {:else if address.x && address.y}
                <div class="city bold">
                    {address.getCoordsAsText()}
                </div>
            {:else}
                <div class="empty-address">Brak adresu</div>
            {/if}
        {:else if address.city || address.street}
            <div style="display: flex; flex-direction: column; row-gap: 0;">
                <div class="bold">{address.city}</div>
                <div style="display: flex; align-items: center; column-gap: 5px;">
                    <div class="light">{address.street} {address.house}</div>
                </div>
            </div>
        {:else if address.x && address.y}
            {address.getCoordsAsText()}
        {:else}
            <div class="empty-address">Brak adresu</div>
        {/if}
    </div>

    <div class="date-2"></div>

    <div class="status-2">
        <div style="width: 36px"></div>
        {#if alerts && !routeDirty}
            <VehicleLimitPie {alerts} />
        {/if}
    </div>

    <div class="other dont-break">
        {#if !assignment?.isLocked() && removable && routePointIndex}
            <IconRemoveWarning scale={0.7} on:click={e => {
                onRemove(routePointIndex);
            }}/>
        {/if}
        {#if expandable}
            <IconExpander isOpen={expanded} on:click={() => {
                expanded = !expanded;
                onToggle(routePointIndex, expanded);
            }}/>
        {/if}
    </div>
</div>
